// Forced clean rebuild after package-lock reset
import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Component, ErrorInfo, ReactNode } from "react";
import { HelmetProvider } from 'react-helmet-async';
import React from 'react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import FleetSection from './components/FleetSection';
import ServicesSection from './components/ServicesSection';
import ReviewsSection from './components/ReviewsSection';
import Footer from './components/Footer';
import './App.css';
import CityPage from './pages/CityPage';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import WhyChooseProfessionalChauffeurService from './pages/blog/why-choose-professional-chauffeur-service';
import AirportTransportationGuide from './pages/blog/airport-transportation-guide';
import CorporateTransportationGuide from './pages/blog/corporate-transportation-guide';

/* Insert global declaration for gtag (GA4) */
declare global {
  interface Window {
    gtag: (command: string, ...args: any[]) => void;
  }
}

// Lazy load pages
import Index from "./pages/Index";
import About from "./pages/About";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import ChauffeurBoston from "./pages/ChauffeurBoston";
import ChauffeurMassachusetts from "./pages/ChauffeurMassachusetts";
import ChauffeurNewYork from "./pages/ChauffeurNewYork";
import ChauffeurPhiladelphia from "./pages/ChauffeurPhiladelphia";
import ChauffeurPortland from "./pages/ChauffeurPortland";
import ChauffeurWashingtonDC from "./pages/ChauffeurWashingtonDC";
import ChauffeurCambridge from "./pages/ChauffeurCambridge";
import ChauffeurAlbany from "./pages/ChauffeurAlbany";
import ChauffeurPrinceton from "./pages/ChauffeurPrinceton";
import ChauffeurPittsburgh from "./pages/ChauffeurPittsburgh";
import ChauffeurBangor from "./pages/ChauffeurBangor";
import ExecutiveCarServiceNewark from "./pages/ExecutiveCarServiceNewark";
import FBOChauffeur from "./pages/FBOChauffeur";
import HourlyChauffeur from "./pages/HourlyChauffeur";
import NotFound from "./pages/NotFound";

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-center text-white p-8">
            <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
            <p className="mb-4">Please try refreshing the page</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-gold text-black px-6 py-2 rounded-full hover:bg-gold/80 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Create a client
const queryClient = new QueryClient();

const App = () => {
  console.log('App component rendering');

  // Add error logging
  React.useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      console.error('Global error caught:', error);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter basename="/">
              <div className="bg-black min-h-screen">
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/chauffeur-boston-ma" element={<ChauffeurBoston />} />
                  <Route path="/chauffeur-massachusetts" element={<ChauffeurMassachusetts />} />
                  <Route path="/chauffeur-new-york-ny" element={<ChauffeurNewYork />} />
                  <Route path="/chauffeur-philadelphia-pa" element={<ChauffeurPhiladelphia />} />
                  <Route path="/chauffeur-portland-me" element={<ChauffeurPortland />} />
                  <Route path="/chauffeur-washington-dc" element={<ChauffeurWashingtonDC />} />
                  <Route path="/chauffeur-cambridge-ma" element={<ChauffeurCambridge />} />
                  <Route path="/chauffeur-albany-ny" element={<ChauffeurAlbany />} />
                  <Route path="/chauffeur-princeton-nj" element={<ChauffeurPrinceton />} />
                  <Route path="/chauffeur-pittsburgh-pa" element={<ChauffeurPittsburgh />} />
                  <Route path="/chauffeur-bangor-me" element={<ChauffeurBangor />} />
                  <Route path="/executive-car-service-newark-nj" element={<ExecutiveCarServiceNewark />} />
                  <Route path="/fbo-private-airport-chauffeur" element={<FBOChauffeur />} />
                  <Route path="/hourly-as-directed-chauffeur" element={<HourlyChauffeur />} />
                  <Route path="/chauffeur-:cityId" element={<CityPage />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/blog/why-choose-professional-chauffeur-service" element={<WhyChooseProfessionalChauffeurService />} />
                  <Route path="/blog/airport-transportation-guide" element={<AirportTransportationGuide />} />
                  <Route path="/blog/corporate-transportation-guide" element={<CorporateTransportationGuide />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </div>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
//lol